import {
  type ContentObjectApi,
  type ContentObjectOption,
  ContentObjectOptions,
  type ContentObjectProps,
  type IntersectFormState,
} from '@openx/types/targeting/contentObject.ts';
import type { TargetingProps } from '@openx/types/targeting/targeting.ts';
import type { UseContentObjectOptionsConfig } from '@openx/types/targeting/targetingHookTypes.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { type JSX, useMemo, useReducer, useState } from 'react';
import { TargetingFieldPathProvider, useTargetingContext } from '../../utils/context/TargetingContext.tsx';
import { useDeferredFieldUpdate } from '../../utils/hooks/useDeferredFieldUpdate.ts';
import { usePreselectableDrawer } from '../shared/hooks/usePreselectableDrawer.ts';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer.tsx';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer.tsx';
import { ContentObjectItems } from './ContentObjectItems/ContentObjectItems.tsx';
import { ContentObjectReadOnly } from './ContentObjectReadOnly.tsx';
import { ContentObjectTopBox } from './ContentObjectTopBox/ContentObjectTopBox.tsx';
import { hasHierarchyOptions, mapContentObjectStateToApi, mapContentObjectToFormState } from './utils/utils.ts';

const reducer = (
  state: ContentObjectProps,
  action: { type: ContentObjectOption | 'ALL'; values }
): ContentObjectProps => {
  const { type, values } = action;
  const param = state[type] as IntersectFormState;
  const op = param?.op || Intersect.INTERSECTS;

  switch (type) {
    case ContentObjectOptions.LANGUAGE:
    case ContentObjectOptions.EPISODE:
      return {
        ...state,
        [type]: {
          op,
          val: values,
        },
      };
    case ContentObjectOptions.CONTENT_RATING:
    case ContentObjectOptions.GENRE:
    case ContentObjectOptions.LIVESTREAM:
      return {
        ...state,
        [type]: values,
      };
    case ContentObjectOptions.NETWORK:
    case ContentObjectOptions.CHANNEL:
    case ContentObjectOptions.SERIES:
      return {
        ...state,
        [type]: {
          op,
          val: new Set([...(state[type]?.val || []), values]),
        },
      };
    case 'ALL':
      if (typeof values === 'function') return values(state);
      return { ...values };
    default:
      return state;
  }
};

// seems like this workaround would not be needed anymore, to be reviewed in EXCH-9617
export const ContentObject = (props: TargetingProps<ContentObjectApi>): JSX.Element => {
  const { useContentObjectOptionsConfig } = useTargetingContext();

  if (!useContentObjectOptionsConfig) {
    throw new Error('useContentObjectOptionsConfig is not provided');
  }

  return <ContentObjectRenderer {...props} useContentObjectOptionsConfig={useContentObjectOptionsConfig} />;
};

const ContentObjectRenderer = ({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  useContentObjectOptionsConfig,
}: TargetingProps<ContentObjectApi> & {
  useContentObjectOptionsConfig: UseContentObjectOptionsConfig;
}): JSX.Element => {
  const { useOptionFetch } = useContentObjectOptionsConfig();

  const { options: genreOptions } = useOptionFetch(
    ContentObjectOptions.GENRE,
    hasHierarchyOptions(targetingParams?.[ContentObjectOptions.GENRE])
  );

  const { options: contentratingOptions } = useOptionFetch(
    ContentObjectOptions.CONTENT_RATING,
    hasHierarchyOptions(targetingParams?.[ContentObjectOptions.CONTENT_RATING])
  );

  const initState = useMemo(
    () =>
      mapContentObjectToFormState(cloneDeep(targetingParams), {
        contentrating: contentratingOptions,
        genre: genreOptions,
      }),
    [targetingParams, contentratingOptions, genreOptions]
  );

  const [formState, dispatch] = useReducer(reducer, initState);
  const [type, setType] = useState<ContentObjectOption>(ContentObjectOptions.GENRE);

  const scheduleDeferredUpdate = useDeferredFieldUpdate(field, onFieldUpdate, mapContentObjectStateToApi);

  const {
    isOpen,
    onDrawerClose,
    onDrawerOpen: handleOnDrawerOpen,
    onDrawerApply,
  } = useDrawer({
    onApply: () => onFieldUpdate(field, mapContentObjectStateToApi(formState)),
    restoreParams: () => dispatch({ type: 'ALL', values: initState }),
  });

  const onDrawerOpen = () => {
    handleOnDrawerOpen();
    dispatch({ type: 'ALL', values: initState });
  };

  const isValidFormat = (type: string): type is ContentObjectOption => {
    return Object.values(ContentObjectOptions).includes(type as ContentObjectOption);
  };

  const openDrawerWithPreselectedType = usePreselectableDrawer<ContentObjectOption>(
    isValidFormat,
    onDrawerOpen,
    setType
  );

  const isDataUpdated = useMemo(() => !isEqual(initState, formState), [initState, formState]);
  const updateFormState = values => dispatch({ type: 'ALL', values });

  const onRemoveParameter = (option: ContentObjectOption) => {
    const defaults = mapContentObjectToFormState(null);
    const next = {
      ...formState,
      [option]: defaults[option],
    };

    dispatch({ type: 'ALL', values: next });

    // Defer the field update to avoid setState-inside-setState
    scheduleDeferredUpdate(next);
  };

  return (
    <TargetingFieldPathProvider value={field}>
      <ContentObjectReadOnly
        name={name}
        readonly={readonly}
        targetingParams={initState}
        onOpenDrawerClick={openDrawerWithPreselectedType}
        onRemoveClick={() => {
          dispatch({ type: 'ALL', values: mapContentObjectToFormState(null) });
          onFieldUpdate(field, null);
        }}
        field={field}
        onRemoveParameter={onRemoveParameter}
      />

      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <ContentObjectTopBox formState={formState} dispatch={dispatch} type={type} setType={setType} />
          <ContentObjectItems targetingParams={formState} onChange={updateFormState} readonly={!!readonly} />
        </TargetingDrawer>
      )}
    </TargetingFieldPathProvider>
  );
};
